import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { ChevronDown, Footprints } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useCurrentWorkspace } from "@/hooks/use-current-workspace";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MapView } from "@/components/map-view";
import { getGetWorkspaceQueryKey, useUpdateWorkspace } from "@/api/orval";
import { ACTIVITY_OPTIONS, activityVerb, toggleActivity } from "@/lib/activity";
import { ShareDialog } from "../components/share-dialog";
import { ProgressCard } from "../components/progress-card";

export const City = () => {
  const { city } = useParams<{ city: string }>();
  const { currentUser } = useAuth();
  const { workspace, workspaceId } = useCurrentWorkspace();
  const queryClient = useQueryClient();

  const [hasCopied, setHasCopied] = useState(false);
  const updateWorkspace = useUpdateWorkspace();
  const activityTypes = workspace?.activity_types ?? ["run"];

  const toggleActivityType = (key: string) => {
    if (!workspaceId) return;
    const next = toggleActivity(activityTypes, key);
    updateWorkspace.mutate(
      { pathParams: { workspaceId }, data: { activity_types: next } },
      {
        onSuccess: () =>
          queryClient.invalidateQueries({
            queryKey: getGetWorkspaceQueryKey({ workspaceId }),
          }),
      },
    );
  };
  const handleCopyLink = () => {
    const url = `${window.location.origin}/${currentUser?.username}/${city}`;
    navigator.clipboard.writeText(url);
    setHasCopied(true);
  };

  useEffect(() => {
    setTimeout(() => setHasCopied(false), 2000);
  }, [hasCopied]);

  return (
    <div className="flex h-screen w-full flex-col">
      <div className="border-b bg-white shadow-sm">
        <div className="mx-auto flex h-14 items-center justify-between pl-4 pr-10">
          <div className="flex items-center gap-2">
            <h1 className="text-sm font-medium">{workspace?.name}</h1>
          </div>
          <div className="flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <div className="flex items-center gap-2">
                    <Footprints className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium capitalize text-gray-500">
                      {activityVerb(activityTypes)}
                    </span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Activities counted</DropdownMenuLabel>
                {ACTIVITY_OPTIONS.map((a) => (
                  <DropdownMenuCheckboxItem
                    key={a.key}
                    checked={activityTypes.includes(a.key)}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={() => toggleActivityType(a.key)}
                  >
                    {a.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <ShareDialog
              user={currentUser}
              slug={city}
              hasCopied={hasCopied}
              handleCopyLink={handleCopyLink}
            />
          </div>
        </div>
      </div>

      <div className="relative flex-1">
        {workspace ? <MapView /> : <div className="h-full w-full bg-muted" />}
        <ProgressCard />
      </div>
    </div>
  );
};
